import React, { useEffect, useMemo, useState } from 'react';
import { getMemberAccessToken } from '../auth/supabase';
import {
  activeUnitRecords,
  countInventoryUnits,
  inventoryCatalogForTarget,
} from '../inventory/inventoryUnitModel';
import { deploymentUsageItems } from './deploymentUsageModel';
import { cacheOfflineInventory, readOfflineInventory, supportsOfflineInventory } from './offlineInventoryCache';

const DETAILED_USAGE_PATTERNS = [/trauma dressings?/i];

function formatDate(value) {
  if (!value) return '';
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value));
  if (!match) return String(value);
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 12, 0, 0, 0);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
}

function formatCachedAt(value) {
  const date = new Date(value || '');
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function cleanWholeNumber(value) {
  const parsed = Number(String(value ?? '').trim());
  return Number.isInteger(parsed) && parsed >= 0 ? parsed : null;
}

function isDetailedUsageRow(row) {
  const text = `${String(row?.item || '')} ${String(row?.component || '')}`;
  return DETAILED_USAGE_PATTERNS.some(pattern => pattern.test(text));
}

function parseDetailedUsage(baseItem, currentItem, currentQuantity) {
  const base = String(baseItem || '').trim();
  const item = String(currentItem || '').trim();
  const prefix = `${base} — `;
  if (item.startsWith(prefix)) {
    const parsed = item.slice(prefix.length)
      .split(';')
      .map(part => part.trim())
      .filter(Boolean)
      .map(part => {
        const match = /^(.*?)\s+[×x]\s*(\d+)$/i.exec(part);
        return match ? { name: match[1].trim(), quantity: match[2] } : { name: part, quantity: '1' };
      });
    if (parsed.length) return parsed;
  }
  if (Number(currentQuantity) > 0) {
    return [{ name: 'Previously reported — type not specified', quantity: String(currentQuantity) }];
  }
  return [{ name: '', quantity: '1' }];
}

function buildDetailedItemLabel(baseItem, lines) {
  const base = String(baseItem || '').trim();
  const summary = lines.map(line => `${line.name.trim()} × ${line.quantity}`).join('; ');
  return summary ? `${base} — ${summary}` : base;
}

export default function GearInventoryView({ asset, deployment, state, actions, actorName, onBack }) {
  const [payload, setPayload] = useState({ catalog: [], expirations: [] });
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [cachedAt, setCachedAt] = useState('');
  const [quantityDrafts, setQuantityDrafts] = useState({});
  const [detailEditor, setDetailEditor] = useState({ catalogId: '', lines: [] });

  useEffect(() => {
    if (!supportsOfflineInventory(asset?.id)) {
      setStatus('unsupported');
      return undefined;
    }

    let cancelled = false;
    const useCachedCopy = fallbackError => {
      const cached = readOfflineInventory(asset.id);
      if (!cached) {
        if (!cancelled) {
          setError(fallbackError || 'This bag inventory has not been saved for offline use yet. Open My Gear with service before the assignment.');
          setStatus('error');
        }
        return false;
      }
      if (!cancelled) {
        setPayload({ catalog: cached.catalog, expirations: cached.expirations });
        setCachedAt(cached.cachedAt || '');
        setError('');
        setStatus('offline');
      }
      return true;
    };

    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      useCachedCopy('This bag inventory is not cached on this device yet.');
      return () => { cancelled = true; };
    }

    setStatus('loading');
    setError('');
    getMemberAccessToken()
      .then(token => {
        if (!token) throw new Error('Member sign in required.');
        return fetch(`/api/member-tracking?mode=inventory-asset&assetIds=${encodeURIComponent(asset.id)}`, {
          cache: 'no-store',
          headers: { Authorization: `Bearer ${token}` },
        });
      })
      .then(async response => {
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data?.error || 'Unable to load this bag inventory.');
        if (!cancelled) {
          const nextPayload = {
            catalog: Array.isArray(data?.catalog) ? data.catalog : [],
            expirations: Array.isArray(data?.expirations) ? data.expirations : [],
          };
          cacheOfflineInventory([asset.id], nextPayload);
          setPayload(nextPayload);
          setCachedAt(new Date().toISOString());
          setStatus('ready');
        }
      })
      .catch(err => {
        if (!cancelled) useCachedCopy(err?.message || 'Unable to load this bag inventory.');
      });
    return () => { cancelled = true; };
  }, [asset?.id]);

  const usage = useMemo(() => deployment ? deploymentUsageItems(state, deployment.id).filter(item => item.assetId === asset.id) : [], [state, deployment, asset.id]);
  const usageByCatalog = useMemo(() => {
    const grouped = new Map();
    usage.forEach(item => grouped.set(item.catalogId, Number(grouped.get(item.catalogId) || 0) + Number(item.quantity || 0)));
    return grouped;
  }, [usage]);
  const usageRecordByCatalog = useMemo(() => new Map(usage.map(item => [item.catalogId, item])), [usage]);
  const scopedCatalog = useMemo(() => inventoryCatalogForTarget(payload.catalog, asset.id), [payload.catalog, asset.id]);

  const rows = useMemo(() => scopedCatalog.map(item => {
    const records = activeUnitRecords(payload.expirations, asset.id, item.catalogId);
    const packed = countInventoryUnits(records);
    const required = Number(String(item.requiredQty || '').replace(/[^0-9.-]/g, ''));
    const used = Number(usageByCatalog.get(item.catalogId) || 0);
    const dated = records.map(record => String(record.expirationDate || '')).filter(Boolean).sort();
    return {
      ...item,
      packed,
      required: Number.isFinite(required) && required >= 0 ? required : null,
      used,
      expected: Math.max(0, packed - used),
      earliestExpiration: dated[0] || '',
    };
  }), [scopedCatalog, payload.expirations, asset.id, usageByCatalog]);

  const adjustUsage = (row, delta, itemOverride = '') => {
    setError('');
    setMessage('');
    try {
      actions.reportUsage({
        deploymentId: deployment.id,
        assetId: asset.id,
        assetName: asset.name,
        catalogId: row.catalogId,
        component: row.component,
        item: itemOverride || row.item,
        quantityDelta: delta,
        actor: actorName,
      });
      const offline = status === 'offline' || (typeof navigator !== 'undefined' && navigator.onLine === false);
      const amount = Math.abs(delta);
      if (offline) {
        setMessage(delta > 0
          ? `${amount} ${row.item} saved offline. It will sync when service returns.`
          : `${amount} ${row.item} usage correction saved offline. It will sync when service returns.`);
      } else {
        setMessage(delta > 0
          ? `${amount} ${row.item} marked used.`
          : `${amount} ${row.item} removed from reported usage.`);
      }
      return true;
    } catch (err) {
      setError(err?.message || 'Unable to update reported usage.');
      return false;
    }
  };

  const quantityValue = row => quantityDrafts[row.catalogId] ?? '1';

  const updateQuantityDraft = (row, value) => {
    setQuantityDrafts(current => ({ ...current, [row.catalogId]: value }));
  };

  const addUsageAmount = row => {
    const amount = cleanWholeNumber(quantityValue(row));
    if (amount === null || amount < 1) return setError('Enter a whole-number amount greater than zero.');
    if (amount > row.expected) return setError(`Only ${row.expected} ${row.item} remain available to report used.`);
    if (adjustUsage(row, amount)) updateQuantityDraft(row, '1');
  };

  const removeUsageAmount = row => {
    const amount = cleanWholeNumber(quantityValue(row));
    if (amount === null || amount < 1) return setError('Enter a whole-number amount greater than zero.');
    if (amount > row.used) return setError(`Only ${row.used} ${row.item} are currently reported used.`);
    const currentItem = usageRecordByCatalog.get(row.catalogId)?.item || row.item;
    if (adjustUsage(row, -amount, currentItem)) updateQuantityDraft(row, '1');
  };

  const openDetailEditor = row => {
    const current = usageRecordByCatalog.get(row.catalogId);
    setDetailEditor({
      catalogId: row.catalogId,
      lines: parseDetailedUsage(row.item, current?.item, current?.quantity || 0),
    });
    setError('');
    setMessage('');
  };

  const updateDetailLine = (index, field, value) => {
    setDetailEditor(current => ({
      ...current,
      lines: current.lines.map((line, lineIndex) => lineIndex === index ? { ...line, [field]: value } : line),
    }));
  };

  const addDetailLine = () => {
    setDetailEditor(current => ({ ...current, lines: [...current.lines, { name: '', quantity: '1' }] }));
  };

  const removeDetailLine = index => {
    setDetailEditor(current => ({ ...current, lines: current.lines.filter((_, lineIndex) => lineIndex !== index) }));
  };

  const saveDetailedUsage = row => {
    const normalized = detailEditor.lines
      .map(line => ({ name: String(line.name || '').trim(), quantity: cleanWholeNumber(line.quantity) }))
      .filter(line => line.name || Number(line.quantity) > 0);

    if (normalized.some(line => !line.name || line.quantity === null || line.quantity < 1)) {
      return setError('Enter the dressing type and a whole-number amount on each line.');
    }

    const total = normalized.reduce((sum, line) => sum + line.quantity, 0);
    if (total > row.packed) return setError(`The detailed total cannot exceed the ${row.packed} items packed in this section.`);

    const current = usageRecordByCatalog.get(row.catalogId);
    const currentItem = current?.item || row.item;
    const nextItem = buildDetailedItemLabel(row.item, normalized);
    const delta = total - row.used;

    if (delta === 0 && nextItem === currentItem) {
      setMessage(`${row.item} usage details are already current.`);
      setDetailEditor({ catalogId: '', lines: [] });
      return;
    }

    if (delta === 0 && row.used > 0) {
      if (!adjustUsage(row, -row.used, currentItem)) return;
      if (!adjustUsage(row, row.used, nextItem)) return;
    } else if (delta !== 0) {
      if (!adjustUsage(row, delta, total > 0 ? nextItem : currentItem)) return;
    }

    setDetailEditor({ catalogId: '', lines: [] });
    setMessage(total > 0 ? `${row.item} details saved · ${total} total reported used.` : `${row.item} usage cleared.`);
  };

  const inventoryVisible = status === 'ready' || status === 'offline';

  return (
    <section className="tracking-card my-gear-inventory-card">
      <div className="tracking-section-head">
        <div>
          <h2>{asset.name}</h2>
          <p>{asset.id}{deployment ? ` · ${deployment.incident}` : ''}</p>
        </div>
        <button type="button" className="tracking-link-btn" onClick={onBack}>Back</button>
      </div>

      {status === 'offline' && (
        <div className="tracking-identity-note">
          <strong>Offline field copy.</strong>{' '}
          You can mark supplies used now. Changes stay on this device and sync automatically when service returns.
          {cachedAt ? ` Inventory saved ${formatCachedAt(cachedAt)}.` : ''}
        </div>
      )}
      {message && <div className="tracking-success">{message}</div>}
      {error && <div className="tracking-error">{error}</div>}
      {status === 'loading' && <div className="tracking-empty">Loading the physical inventory saved for this gear…</div>}
      {status === 'unsupported' && <div className="tracking-empty">This is accountable gear, but it does not have a consumable inventory checklist.</div>}

      {inventoryVisible && (
        <div className="my-gear-inventory-list">
          {rows.map(row => {
            const detailed = isDetailedUsageRow(row);
            const currentUsage = usageRecordByCatalog.get(row.catalogId);
            const detailOpen = detailEditor.catalogId === row.catalogId;
            return (
              <div className="my-gear-inventory-row" key={row.catalogId}>
                <div className="my-gear-inventory-copy">
                  <strong>{row.item}</strong>
                  <small>{row.component || 'Other'} · Packed {row.packed}{row.required === null ? '' : ` / Standard ${row.required}`}{row.earliestExpiration ? ` · Earliest exp ${formatDate(row.earliestExpiration)}` : ''}</small>
                  {deployment && row.used > 0 && <small className="my-gear-used-note">Reported used {row.used} · Expected remaining {row.expected}</small>}
                  {detailed && currentUsage?.item && currentUsage.item !== row.item && <small className="my-gear-detail-summary">{currentUsage.item}</small>}
                </div>

                {deployment && !detailed && (
                  <div className="my-gear-use-actions">
                    <label className="my-gear-quantity-field">
                      <span>Amount used</span>
                      <input
                        value={quantityValue(row)}
                        onChange={event => updateQuantityDraft(row, event.target.value)}
                        inputMode="numeric"
                        min="1"
                        step="1"
                        aria-label={`Amount of ${row.item} used`}
                      />
                    </label>
                    <button type="button" className="tracking-btn tracking-btn-secondary" onClick={() => addUsageAmount(row)} disabled={row.expected <= 0}>Confirm used</button>
                    {row.used > 0 && <button type="button" className="tracking-link-btn" onClick={() => removeUsageAmount(row)}>Undo amount</button>}
                  </div>
                )}

                {deployment && detailed && (
                  <div className="my-gear-use-actions">
                    <button type="button" className="tracking-btn tracking-btn-secondary" onClick={() => detailOpen ? setDetailEditor({ catalogId: '', lines: [] }) : openDetailEditor(row)}>
                      {detailOpen ? 'Close details' : row.used > 0 ? 'Edit used details' : 'Report used details'}
                    </button>
                  </div>
                )}

                {deployment && detailed && detailOpen && (
                  <div className="my-gear-detail-editor">
                    <div className="my-gear-detail-editor-head">
                      <div>
                        <strong>What trauma dressings were used?</strong>
                        <small>Enter one item per line with the amount used. Saving replaces the current detailed total for this category.</small>
                      </div>
                    </div>
                    <div className="my-gear-detail-lines">
                      {detailEditor.lines.map((line, index) => (
                        <div className="my-gear-detail-line" key={`${row.catalogId}-${index}`}>
                          <label>
                            <span>Item used</span>
                            <input value={line.name} onChange={event => updateDetailLine(index, 'name', event.target.value)} placeholder="4x4 gauze, ABD pad, Israeli dressing…" />
                          </label>
                          <label className="my-gear-detail-qty">
                            <span>Amount</span>
                            <input value={line.quantity} onChange={event => updateDetailLine(index, 'quantity', event.target.value)} inputMode="numeric" min="1" step="1" />
                          </label>
                          <button type="button" className="tracking-link-btn" onClick={() => removeDetailLine(index)}>Remove</button>
                        </div>
                      ))}
                    </div>
                    <div className="my-gear-detail-actions">
                      <button type="button" className="tracking-link-btn" onClick={addDetailLine}>+ Add another line</button>
                      <button type="button" className="tracking-btn tracking-btn-secondary" onClick={() => saveDetailedUsage(row)}>Confirm dressing usage</button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          {!rows.length && <div className="tracking-empty">No inventory standard is configured for this gear yet.</div>}
        </div>
      )}
    </section>
  );
}
